import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE = '/machinesim/api';

const ProgramManager = ({ onClose }) => {
  const [programs, setPrograms] = useState({});
  const [loading, setLoading] = useState(true);
  const [newProgram, setNewProgram] = useState({ programNumber: '', baseRunTime: '' });

  // Fetch programs data
  const fetchPrograms = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/programs`);
      setPrograms(response.data);
    } catch (error) {
      console.error('Failed to fetch programs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrograms();
  }, []);

  // Add or update program
  const handleAddProgram = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_BASE}/programs`, {
        programNumber: newProgram.programNumber,
        baseRunTime: parseInt(newProgram.baseRunTime)
      });
      
      // Refresh programs list
      await fetchPrograms();
      
      // Clear form
      setNewProgram({ programNumber: '', baseRunTime: '' });
    } catch (err) {
      console.error('Failed to add program:', err);
      alert('Failed to add program: ' + (err.response?.data?.error || err.message));
    }
  };

  // Delete program
  const handleDeleteProgram = async (programNumber) => {
    if (!confirm(`Are you sure you want to delete program ${programNumber}?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API_BASE}/programs/${programNumber}`);
      await fetchPrograms();
    } catch (err) {
      console.error('Failed to delete program:', err);
      alert('Failed to delete program: ' + (err.response?.data?.error || err.message));
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-gray-800 p-6 rounded-lg">
          <div className="text-white">Loading programs...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg border-2 border-gray-600 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-600">
          <h2 className="text-white text-2xl font-bold">Program Database Manager</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-3xl"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Add New Program Form */}
          <div className="bg-gray-700 p-4 rounded-lg border border-gray-600 mb-6">
            <h3 className="text-white text-lg font-bold mb-4">Add New Program</h3>
            <form onSubmit={handleAddProgram}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-gray-300 text-sm block mb-2">Program Number:</label>
                  <input
                    type="text"
                    value={newProgram.programNumber}
                    onChange={(e) => setNewProgram({ ...newProgram, programNumber: e.target.value })}
                    placeholder="e.g., O0004"
                    className="w-full bg-gray-600 text-white px-3 py-2 rounded border border-gray-500"
                    required
                  />
                </div>
                <div>
                  <label className="text-gray-300 text-sm block mb-2">Base Run Time (seconds):</label>
                  <input
                    type="number"
                    value={newProgram.baseRunTime}
                    onChange={(e) => setNewProgram({ ...newProgram, baseRunTime: e.target.value })}
                    placeholder="e.g., 300"
                    className="w-full bg-gray-600 text-white px-3 py-2 rounded border border-gray-500"
                    min="1"
                    required
                  />
                </div>
              </div>
              <button
                type="submit"
                className="w-full md:w-auto px-6 py-2 bg-green-600 hover:bg-green-700 rounded text-white font-medium"
              >
                Add/Update Program
              </button>
            </form>
          </div>

          {/* Programs List */}
          <div className="bg-gray-700 p-4 rounded-lg border border-gray-600">
            <h3 className="text-white text-lg font-bold mb-4">Current Programs</h3>
            {Object.keys(programs).length === 0 ? (
              <p className="text-gray-400 text-center py-8">No programs found</p>
            ) : (
              <div className="space-y-3">
                {Object.entries(programs).map(([programNumber, programData]) => (
                  <div key={programNumber} className="bg-gray-600 p-4 rounded-lg border border-gray-500">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-4">
                        <div className="text-green-400 font-mono font-bold text-lg">
                          {programNumber}
                        </div>
                        <div className="text-gray-300">
                          <span className="text-gray-400">Run Time:</span> {programData.baseRunTime}s
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteProgram(programNumber)}
                        className="px-3 py-1 bg-red-600 hover:bg-red-700 rounded text-white text-sm font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramManager; 